import { api } from './api';
import type { PaginatedResult } from './documentWorkflowService';

export interface DocumentAuditDto {
  id: string;
  documentId: string;
  category: string;
  documentName: string;
  actionName: string;
  actionDescription: string;
  actionDetails: string;
  userId: string;
  userName: string;
  email: string;
  createdAt: string;
}

function unwrapAxiosMessage(err: unknown): string {
  const e = err as { response?: { data?: { message?: string } }; message?: string };
  return e?.response?.data?.message || e?.message || 'Erro na requisição';
}

class DocumentAuditService {
  async getByDocument(
    documentId: string,
    options?: { category?: string | null; pageSize?: number }
  ): Promise<DocumentAuditDto[]> {
    const pageSize = options?.pageSize ?? 200;
    const category = options?.category;
    try {
      const all: DocumentAuditDto[] = [];
      let page = 1;
      let totalPages = 1;
      do {
        const params: Record<string, string | number> = { page, pageSize };
        if (category) params.category = category;
        const { data } = await api.get<PaginatedResult<DocumentAuditDto>>(
          `/document-audit/${documentId}`,
          { params }
        );
        all.push(...(data?.data ?? []));
        totalPages = data?.totalPages ?? 1;
        page += 1;
      } while (page <= totalPages);

      return all.sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
    } catch (err) {
      throw new Error(unwrapAxiosMessage(err));
    }
  }

  async exportCsv(documentId: string, category?: string | null): Promise<Blob> {
    const params: Record<string, string> = { format: 'csv' };
    if (category) params.category = category;
    const response = await api.get<Blob>(`/document-audit/${documentId}/export`, {
      params,
      responseType: 'blob',
      validateStatus: () => true,
    });
    if (response.status >= 400) {
      let message = 'Erro ao exportar auditoria';
      try {
        const text = await new Response(response.data as Blob).text();
        try {
          const j = JSON.parse(text) as { message?: string };
          if (j.message) message = j.message;
        } catch {
          if (text) message = text;
        }
      } catch {
        /* ignore */
      }
      throw new Error(message);
    }
    return response.data;
  }
}

export const documentAuditService = new DocumentAuditService();
